import { Playlist, Video } from 'api';
import cx from 'classnames';
import { Text } from 'components/@tedui';
import ContentContainer from 'components/content-container';
import { Link } from 'components/router';
import { useDidMount } from 'lib/hooks/useDidMount';
import { useIsMobile } from 'lib/hooks/useIsBreakpointWidth';
import usePersonalizedContent from 'lib/hooks/useSailthru/usePersonalizedContent';
import { orderSailthruContent, SailthruFormattedItem } from 'lib/sailthru';
import { useFallbackData } from 'lib/sailthru/fallback';
import { isEmpty } from 'lodash-es';
import { motion } from 'motion/react';
import { useEffect, useMemo, type JSX } from 'react';
import NumberedCard from './NumberedCard/NumberedCard';
import Skeleton from './Skeleton/NumberedCardRibbonSkeleton';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1
    }
  }
};

const headerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.4, 0.25, 1]
    }
  }
};

const gridVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.4,
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.7,
      ease: [0.25, 0.4, 0.25, 1]
    }
  }
};

// Hover animation for cards
const cardHoverVariants = {
  rest: { y: 0, scale: 1 },
  hover: {
    y: -6,
    scale: 1.02,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  }
};

type PlaylistItem = Playlist & {
  isPlaylist: true;
};

type NumberedCardRibbonProps = {
  talks: Video[] | PlaylistItem[] | SailthruFormattedItem[];
  title: string;
  isSailthru?: boolean;
  sailthruSectionId?: string;
  hasSailthruCookie?: boolean;
  mixpanelTitle: string;
  fallbackSection?: string;
  link?: {
    visible?: boolean;
    text: string;
    url: string;
  };
  wrapperClassName?: string;
  className?: string;
  orderBy?: 'column' | 'row';
};

const getCardNumber = (
  index: number,
  totalItems: number,
  isMobile: boolean,
  orderBy: 'column' | 'row' = 'column'
): number => {
  if (isMobile) {
    // Mobile: single column, sequential numbering
    return index + 1;
  }

  if (orderBy === 'row') {
    // Desktop row ordering: 1,2 | 3,4 | 5,6
    return index + 1;
  }

  // Desktop column ordering: 1,4 | 2,5 | 3,6
  // Even indices (0, 2, 4) go in left column: 1, 2, 3...
  // Odd indices (1, 3, 5) go in right column: starting after left column numbers
  if (index % 2 === 0) {
    // Left column
    return Math.floor(index / 2) + 1;
  } else {
    // Right column
    const leftColumnCount = Math.ceil(totalItems / 2);
    return leftColumnCount + Math.floor(index / 2) + 1;
  }
};

const NumberedCardRibbon = ({
  talks,
  title,
  isSailthru,
  sailthruSectionId,
  hasSailthruCookie = false,
  mixpanelTitle,
  fallbackSection,
  link,
  wrapperClassName,
  className,
  orderBy = 'column'
}: NumberedCardRibbonProps): JSX.Element => {
  const hasContent = !isEmpty(talks);
  const hasMounted = useDidMount();

  const {
    data: fallbackData,
    loading: isFetchingFallbackData,
    fetchFallbackContent
  } = fallbackSection
    ? useFallbackData(fallbackSection)
    : { data: null, loading: false, fetchFallbackContent: null };

  const {
    data: personalizedData,
    refetch: fetchPersonalizedData,
    isFetching: isFetchingPersonalizedData
  } = usePersonalizedContent(sailthruSectionId ?? '');

  useEffect(() => {
    if (fallbackSection && isEmpty(talks)) {
      fetchFallbackContent?.();
    }
  }, [fetchFallbackContent, fallbackSection, talks]);

  useEffect(() => {
    if (hasSailthruCookie && hasMounted) {
      fetchPersonalizedData();
    }
  }, [fetchPersonalizedData, hasSailthruCookie, hasMounted]);

  const talkData = useMemo(() => {
    if (isSailthru) {
      if (!isEmpty(personalizedData)) {
        return orderSailthruContent(personalizedData, 6);
      }

      if (hasContent) {
        return orderSailthruContent(talks as SailthruFormattedItem[], 6);
      }

      return orderSailthruContent(fallbackData as SailthruFormattedItem[], 6);
    }
    return talks;
  }, [talks, fallbackData, hasContent, personalizedData]);

  const { isMobile, isTablet, isDesktop } = useIsMobile({
    breakPointType: 'tui'
  });

  const headerClasses = cx(
    'flex items-center justify-between border-t-thin border-black',
    {
      'pb-4': !isMobile,
      'pb-6': isMobile
    },
    className
  );
  const cardClasses = cx('grid rounded-sm', {
    'gap-0 divide-y-thin': isMobile,
    'gap-y-10': isTablet && talkData.length < 6,
    'grid-cols-2 gap-x-10 gap-y-4':
      isDesktop || (!isMobile && talkData.length > 4)
  });

  if (isFetchingPersonalizedData || isFetchingFallbackData) {
    return <Skeleton headerClasses={headerClasses} isMobileWidth={isMobile} />;
  }

  if (isEmpty(talkData)) {
    return <></>;
  }

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <ContentContainer className={cx('relative pb-12', wrapperClassName)}>
        <motion.div
          id="slide-header"
          className={headerClasses}
          variants={headerVariants}
        >
          <Text
            tag="p"
            isBold
            variant="label2"
            useNewTextStyles
            UNSAFE_className="pt-3"
          >
            {title}
          </Text>

          {link?.visible && !isMobile && (
            <Link isZenithLink href={link.url}>
              <Text
                variant="link2"
                weight="font-semibold"
                useNewTextStyles
                UNSAFE_className="underline hover:no-underline transition-all duration-200"
              >
                {link.text}
              </Text>
            </Link>
          )}
        </motion.div>

        <motion.div className={cardClasses} variants={gridVariants}>
          {talkData.map((talk, index) => {
            const number = getCardNumber(
              index,
              talkData.length,
              isMobile,
              orderBy
            );

            return (
              <motion.div
                key={`Numbered-${number}`}
                variants={cardVariants}
                initial="rest"
                whileHover="hover"
              >
                <motion.div variants={cardHoverVariants}>
                  <NumberedCard
                    item={talk}
                    itemNumber={number}
                    mixpanelTitle={mixpanelTitle}
                  />
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>
      </ContentContainer>
    </motion.div>
  );
};

export default NumberedCardRibbon;
